interface HttpResponse
{
    Status: number,
    Message: string,
    Data: any,
    Redirect: string
}

// Extended interface that matches the full server API response structure
export interface ApiHttpResponse<T = any>
{
    Status: number;
    Message: string;
    Redirect: string;
    Meta: {
        Total?: number;
        Filter?: any;
    };
    Data: T;
}

interface HttpRequest
{
    url: string,
    headers: any,
    method: string,
    params: any,
    data: any
}

import axios from "axios";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Toast from "@/services/Toast";

const live_base = "https://admin.7thleveltech.com/v1";
const dev_base = "http://localhost:5033/v1";
const dbg_base = "http://localhost:5000/v1";

// Helper to get the current API base URL
export const getApiBaseUrl = (): string => {
    if (-1 < location.href.indexOf("admin.7thleveltech.com")) {
        return live_base;
    } else {
        if (DEBUG || -1 < location.href.indexOf("localhost:5000")) {
            return dbg_base;
        } else {
            return dev_base;
        }
    }
};

const DEBUG = false;

/**
 * Makes an HTTP request to
 * @param url 
 * @param method 
 * @param params 
 * @param dta 
 * @param enctype 
 * @returns 
 */
const makeRequest = async (
    url:string, 
    method:string, 
    params?:any, 
    dta?:any, 
    enctype?:string,
    fullResponse?:boolean
):Promise<any> =>
{

    // if we have a protocol in the URL, do NOT use the base.
    if (url.indexOf("://") < 0)
    {
        // make sure url has leading slash and remove any double slashes
        url = ("/" + url).replace(/\/\//g, "/");

        // live 
        if (-1 < location.href.indexOf("admin.7thleveltech.com"))
        {
            url = live_base + url;
        } 

        // or dev
        else
        {
            if (DEBUG || -1 < location.href.indexOf("localhost:5000"))
            {
                url = dbg_base + url;
            }
            else
            {
                url = dev_base + url;
            }
        }
    }

    let bearer = Cache.get("bearer") || "";
    let headers = {} as any;

    headers["Authorization"] = `Bearer ${bearer}`;

    if (enctype)
    {
        headers["Content-Type"] = enctype;
    }

    // set all empty strings to null for string constraints 
    for (let p in dta)
    {
        if (dta[p] === "" || dta[p] === undefined)
        {
            dta[p] = null;
        }
    }

    console.debug("axios", dta);

    try
    {
        let { data }:{ data:HttpResponse } = await axios({
            url: url,
            headers: headers,
            method: method,
            params: params,
            paramsSerializer: {
                indexes: null, // no brackets at all
            },
            data: dta,
        });

        // failure status ...
        if (!data.Status)
        {
            const errorMsg = data.Message || "Server returned error status";
            console.error("HTTP Request Failed:", {
                url: url,
                method: method,
                status: data.Status,
                message: errorMsg,
                fullResponse: data
            });
            throw new Error(`${errorMsg} (URL: ${url})`);
        }

        if (data.Redirect)
        {
            // redirect the app to 
            Emitter.emit("redirect", data.Redirect);
            return;
        }

        if (data.Message)
        {
            Toast.success(data.Message);
        }

        return fullResponse ? data : (data.Data || null);
    }

    catch (err:any)
    {
        // first, reset any spinners as the application won't 
        // continue normal program flow from here...
        setTimeout(() =>
        {
            Emitter.emit("loading", -1);
        }, 300);

        // log the error for review with full context...
        console.error("HTTP Request Error:", {
            url: url,
            method: method,
            params: params,
            data: dta,
            error: err,
            errorMessage: err?.message,
            response: err?.response?.data
        });

        // now ... lots of different error styles ...

        // 1) No error was supplied, not sure if this is possible ...
        if (!err)
        {
            const msg = `An HTTP error occurred (URL: ${url})`;
            Toast.warning(msg);
            throw new Error(msg);
        }

        // 2) Check if Axios error
        if (err.response && err.response.data)
        {
            let data = err.response.data;
            let status = err.response.status;

            // sometimes ASP.NET returns errors on an "errors" object ...
            if (data.errors)
            {
                let msg = "";
                let num = 0;
                for (let p in data.errors)
                {
                    for (let i = 0; i < data.errors[p].length; i++)
                    {
                        if (0 < num) 
                        {
                            msg += " ";
                        }
        
                        msg += data.errors[p][i];
                        num++;
                    }
                }

                Toast.warning(msg);
                throw new Error(msg);
            }

            // sometime ASP.NET returns errors in an array ...
            else if (data.length)
            {
                let msg = "";
                for (let i = 0; i < data.length; i++)
                {
                    if (0 < i) {
                        msg += " ";
                    }

                    msg += data[i].Description;
                }

                Toast.warning(msg);
                throw new Error(msg);
            }

            else if (data.Message || data.message)
            {
                const msg = data.Message || data.message;
                if (msg === "Not authorized")
                {
                    Emitter.emit("redirect", "/logout");
                }

                console.error(`API Error (${method.toUpperCase()} ${url}):`, msg);
                Toast.warning(msg);
                throw new Error(`${msg} (URL: ${url})`);
            }

            // check the server response status
            if (status === 401)
            {
                const msg = `Not authorized (${method.toUpperCase()} ${url})`;
                console.error(msg);
                Toast.warning("Not authorized");
                Emitter.emit("redirect", "/logout");
                throw new Error(msg);
            }
            
            // Generic server error with status code
            console.error(`HTTP ${status} Error (${method.toUpperCase()} ${url}):`, data);
            const genericMsg = `Server error ${status} (URL: ${url})`;
            Toast.warning(genericMsg);
            throw new Error(genericMsg);

        }

        // 3) the API returns [Message] and JavaScript Errors have a [message]
        if (err.Message || err.message)
        {
            const msg = err.Message || err.message;
            console.error(`Error (${method.toUpperCase()} ${url}):`, msg);
            Toast.warning(msg);
            throw err;
        }
        

        // finally, if we reached here ... we have an
        // error object that we're not handling above 
        // rethrow the error for the application ...
        console.error(`Unhandled Error (${method.toUpperCase()} ${url}):`, err);
        const finalMsg = `Request failed: ${url}`;
        Toast.warning(finalMsg);
        throw err;
    }

}

class HttpRequest
{
    async get(url:string, params?:any):Promise<any>
    {
        return await makeRequest(url, "get", params);
    }

    async post(url:string, data?:any, params?:any):Promise<any>
    {
        return await makeRequest(url, "post", params, data);
    }

    async upload(url:string, data?:any, params?:any):Promise<any>
    {
        return await makeRequest(url, "post", params, data, "multipart/form-data");
    }

    async delete(url:string, params?:any):Promise<any>
    {
        return await makeRequest(url, "delete", params);
    }

    async getFullResponse<T = any>(url:string, params?:any):Promise<ApiHttpResponse<T>>
    {
        return await makeRequest(url, "get", params, undefined, undefined, true);
    }
}

const singleton = new HttpRequest();
export default singleton;